/**
 * ConnectOAuthCard — the `connect · kind:'oauth'` entry point of a deployed
 * agent: one press signs the OWNER in to the remote MCP server the agent's
 * template DECLARES (`remoteMcp` + `entryPoints.connect {kind:'oauth'}`), and
 * the platform holds the grant encrypted server-side from then on.
 *
 * GENERIC BY CONSTRUCTION — the cloud twin of the self-host dashboard's
 * `wireConnectOAuth` (selfhosted/ui/index.html; the two UIs share zero code so
 * the flow is implemented twice, per the two-UIs rule). Everything user-facing
 * is the DECLARATION's: the heading, the button words, the one sentence per
 * state, the note. The renderer substitutes nothing and knows no agent by
 * name; a declared string that is absent renders nothing.
 *
 * Flow (same routes self-host drives, now registered on cloud too):
 *   POST /mcp/custom/:uuid/:serverRef/authorize  → { authorizeUrl }  (owner)
 *   GET  /mcp/custom/:uuid/:serverRef/status     → { connected, account, … }
 *   POST /mcp/custom/:uuid/:serverRef/disconnect → { status, revoked, … }
 * The provider's redirect lands on the backend's own public callback
 * (/mcp/custom/callback), so there is no paste-back step here: this page just
 * opens the authorize URL in a new tab and POLLS status until the grant lands.
 *
 * `serverRef` comes from the derived endpoint — the minted entry id once a
 * grant exists, the declared NAME before that; ONE backend resolver accepts
 * both spellings, so this card never needs to know which it holds.
 *
 * Security posture (matches the backend contract):
 *   - no token/secret ever reaches this page — status carries booleans,
 *     timestamps and an optional display identity only;
 *   - `clientWarning` (the development/testing client path) is rendered WORD
 *     FOR WORD when present — hiding it is what would make the provider's
 *     consent screen read as phishing.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../../../services/api';
import ConfirmModal from '../../molecules/ConfirmModal/ConfirmModal';
import './ConnectOAuthCard.css';

// The token a declared `connectedLine` puts where the connected account goes.
// Two-places pair with the backend's agent-entry-points ACCOUNT_PLACEHOLDER.
const ACCOUNT_PLACEHOLDER = '{account}';

const POLL_INTERVAL_MS = 3000;
const POLL_DEADLINE_MS = 150000; // ~2.5 minutes, same bound as self-host

/** The declared sentence for the current state — or '' (render nothing). */
function stateLine(ep, st) {
  const declared = st && st.connected
    ? ep.connectedLine
    : (st && st.authorizePending ? ep.waitingLine : ep.disconnectedLine);
  if (!declared) return '';
  if (declared.indexOf(ACCOUNT_PLACEHOLDER) < 0) return declared;
  const a = (st && st.account) || null;
  const who = a ? (a.email || a.name || '') : '';
  // A line that ASKS for the account and cannot get it renders NOTHING —
  // "Connected to X as ." is worse than an empty slot.
  return who ? declared.split(ACCOUNT_PLACEHOLDER).join(who) : '';
}

const ConnectOAuthCard = ({ pipeline, setToast, onChange }) => {
  // The derived `connect · oauth` endpoint — the declaration that puts this
  // card on the page at all. No declaration ⇒ no card, no requests.
  const ep = useMemo(() => {
    const eps = pipeline?.resources?.endpoints;
    if (!Array.isArray(eps)) return null;
    return eps.find((e) => e && e.kind === 'connect' && e.connectKind === 'oauth' && e.serverRef) || null;
  }, [pipeline]);

  const uuid = pipeline?.uuid || '';
  const serverRef = ep?.serverRef || '';
  const base = uuid && serverRef
    ? `/mcp/custom/${encodeURIComponent(uuid)}/${encodeURIComponent(serverRef)}`
    : '';

  const [status, setStatus] = useState(null);   // last …/status body (null = unknown)
  const [known, setKnown] = useState(false);    // first status answered → actions may paint
  const [busy, setBusy] = useState(false);      // an authorize/disconnect in flight
  const [polling, setPolling] = useState(false);
  const [pollExpired, setPollExpired] = useState(false);
  const [flowError, setFlowError] = useState(null); // { message, code } | null
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [warning, setWarning] = useState('');   // clientWarning, verbatim

  // Timers die with the card (tab switch, agent delete, navigation).
  const timersRef = useRef([]);
  const aliveRef = useRef(true);
  useEffect(() => () => {
    aliveRef.current = false;
    timersRef.current.forEach((t) => clearTimeout(t));
  }, []);
  const later = useCallback((fn, ms) => {
    const t = setTimeout(fn, ms);
    timersRef.current.push(t);
    return t;
  }, []);

  const refresh = useCallback(async () => {
    if (!base) return null;
    try {
      const st = await api.get(`${base}/status`);
      if (!aliveRef.current) return null;
      setStatus(st);
      setKnown(true);
      if (st && st.error) setFlowError({ message: st.error, code: st.code });
      return st;
    } catch (e) {
      if (!aliveRef.current) return null;
      setKnown(true);
      setFlowError({ message: e.message, code: e.body && e.body.code });
      return null;
    }
  }, [base]);

  useEffect(() => { setStatus(null); setKnown(false); setFlowError(null); refresh(); }, [refresh]);

  // Poll …/status until the provider's redirect has landed on the backend.
  const pollForConnect = useCallback(() => {
    const deadline = Date.now() + POLL_DEADLINE_MS;
    setPolling(true);
    setPollExpired(false);
    const tick = () => {
      later(async () => {
        if (!aliveRef.current) return;
        let st = null;
        try { st = await api.get(`${base}/status`); } catch { /* transient — keep polling */ }
        if (!aliveRef.current) return;
        if (st) { setStatus(st); setKnown(true); }
        if (st && st.connected) {
          setPolling(false);
          setFlowError(null);
          setToast?.({ show: true, message: (st.account && (st.account.email || st.account.name)) ? `Connected as ${st.account.email || st.account.name}` : 'Connected', color: 'success' });
          // The sign-in may have CREATED the server on the row (a declared
          // server has no entry until its grant lands) — the page's endpoint
          // list and serverRef changed, so let the page refetch itself.
          onChange?.();
          return;
        }
        if (Date.now() > deadline) { setPolling(false); setPollExpired(true); return; }
        tick();
      }, POLL_INTERVAL_MS);
    };
    tick();
  }, [base, later, onChange, setToast]);

  const handleConnect = useCallback(async () => {
    if (busy || !base) return;
    setBusy(true);
    setFlowError(null);
    setPollExpired(false);
    try {
      const r = await api.post(`${base}/authorize`, {});
      if (!aliveRef.current) return;
      if (r && r.clientWarning) setWarning(r.clientWarning);
      if (r && r.authorizeUrl) {
        window.open(r.authorizeUrl, '_blank', 'noopener,noreferrer');
        setStatus((prev) => ({ ...(prev || {}), connected: false, authorizePending: true }));
        pollForConnect();
      } else {
        setFlowError({ message: 'The server returned no authorize URL.', code: 'mcp_oauth_no_authorize_url' });
      }
    } catch (e) {
      if (aliveRef.current) setFlowError({ message: e.message, code: e.body && e.body.code });
    } finally {
      if (aliveRef.current) setBusy(false);
    }
  }, [base, busy, pollForConnect]);

  const doDisconnect = useCallback(async () => {
    if (busy || !base) return;
    setBusy(true);
    setFlowError(null);
    try {
      const r = await api.post(`${base}/disconnect`, {});
      if (!aliveRef.current) return;
      // Repaint from the SAME status shape every other path uses.
      setStatus((r && r.status) || { connected: false });
      setKnown(true);
      // Which of the two actually happened — reported, never guessed.
      setToast?.({ show: true, message: r && r.revoked ? 'Disconnected — the provider was told to revoke it.' : 'Disconnected', color: 'success' });
      // The entry may be GONE from the row — the endpoint list re-derives.
      if (r && r.entryRemoved) onChange?.();
    } catch (e) {
      if (aliveRef.current) setFlowError({ message: e.message, code: e.body && e.body.code });
    } finally {
      if (aliveRef.current) setBusy(false);
    }
  }, [base, busy, onChange, setToast]);

  if (!ep || !base) return null;

  const connected = !!(status && status.connected);
  const line = stateLine(ep, status);

  return (
    <div className="pipeline-section connect-oauth-card">
      {ep.label && <h3 className="pipeline-section-title">{ep.label}</h3>}
      {warning && <div className="connect-oauth-warning">{warning}</div>}
      <div className="connect-oauth-box">
        <span className="connect-oauth-line">{line}</span>
        {/* EXACTLY ONE ACTION, chosen by the STATE — and none before the first
            status answer, so "state unknown" shows neither button. */}
        {known && (
          connected ? (
            ep.buttonDisconnect ? (
              <button
                type="button"
                className="btn-secondary"
                disabled={busy}
                onClick={() => (ep.disconnectConfirm ? setConfirmOpen(true) : doDisconnect())}
              >
                {ep.buttonDisconnect}
              </button>
            ) : null
          ) : (
            ep.button ? (
              <button type="button" className="btn-primary" disabled={busy || polling} onClick={handleConnect}>
                {ep.button}
              </button>
            ) : null
          )
        )}
      </div>
      {flowError && flowError.message && (
        <div className="connect-oauth-error">
          {flowError.message}
          {flowError.code ? <span className="connect-oauth-error-code">{flowError.code}</span> : null}
        </div>
      )}
      {pollExpired && !connected && (
        <div className="connect-oauth-recheck">
          <button
            type="button"
            className="btn-secondary"
            onClick={async () => { setPollExpired(false); const st = await refresh(); if (st && st.connected) onChange?.(); }}
          >
            Check again
          </button>
        </div>
      )}
      {ep.note && <p className="pipeline-section-hint connect-oauth-note">{ep.note}</p>}
      {confirmOpen && (
        <ConfirmModal
          open
          onClose={() => setConfirmOpen(false)}
          title={ep.buttonDisconnect || 'Disconnect'}
          ariaLabel={ep.buttonDisconnect || 'Disconnect'}
          primaryLabel={ep.buttonDisconnect || 'Disconnect'}
          onPrimary={doDisconnect}
          secondaryLabel="Cancel"
        >
          <p style={{ fontSize: 13, lineHeight: 1.55, textAlign: 'left' }}>{ep.disconnectConfirm}</p>
        </ConfirmModal>
      )}
    </div>
  );
};

export default ConnectOAuthCard;
